import React, { useState, useMemo } from 'react';
import { useQuery } from 'react-query';
import { api } from '../utils/api';
import LoadingSpinner from '../components/LoadingSpinner';
import toast from 'react-hot-toast';
import {
  ExclamationTriangleIcon,
  EyeIcon,
  CheckIcon,
  MapPinIcon,
  CalendarIcon,
  UserIcon,
  TagIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationCircleIcon,
  FunnelIcon,
  ShieldCheckIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';

interface Incident {
  id: number;
  title: string;
  description: string;
  incident_type: string;
  category?: string;
  subcategory?: string;
  priority?: 'low' | 'medium' | 'high' | 'critical';
  severity?: 'low' | 'medium' | 'high' | 'critical';
  status?: 'reported' | 'investigating' | 'resolved' | 'dismissed' | 'uncompliant' | 'sent_for_investigation' | 'sent_for_intervention' | 'documented' | 'intervened';
  district?: string;
  district_id?: number;
  constituency?: string;
  ward?: string;
  center?: string;
  reporter_name?: string;
  reporter_phone?: string;
  created_at: string;
  updated_at?: string;
  attachments?: string[];
  notes?: string;
  [key: string]: any; // Allow additional properties
}

interface IncidentStats {
  total: number;
  reported: number;
  investigating: number;
  resolved: number;
  dismissed: number;
  uncompliant: number;
  sent_for_investigation: number;
  sent_for_intervention: number;
  intervened: number;
  documented: number;
  by_priority: {
    low: number;
    medium: number;
    high: number;
    critical: number;
  };
  by_category: {
    [key: string]: number;
  };
}

interface PreviewModalProps {
  incident: Incident | null;
  isOpen: boolean;
  onClose: () => void;
  onMarkIntervened: (incident: Incident) => void;
  onMarkResolved: (incident: Incident) => void;
}

const PreviewModal: React.FC<PreviewModalProps> = ({ incident, isOpen, onClose, onMarkIntervened, onMarkResolved }) => {
  if (!isOpen || !incident) return null;

  // Debug: Log the incident object to see its structure
  console.log('🔍 Incident object in PreviewModal:', incident);

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'text-red-600 bg-red-50 border-red-200';
      case 'high': return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'medium': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'low': return 'text-blue-600 bg-blue-50 border-blue-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending': return <ClockIcon className="w-4 h-4 text-orange-500" />;
      case 'investigating': return <ExclamationCircleIcon className="w-4 h-4 text-blue-500" />;
      case 'resolved': return <CheckCircleIcon className="w-4 h-4 text-green-500" />;
      case 'dismissed': return <XCircleIcon className="w-4 h-4 text-gray-500" />;
      case 'sent_for_intervention': return <ExclamationTriangleIcon className="w-4 h-4 text-purple-500" />;
      case 'intervened': return <ShieldCheckIcon className="w-4 h-4 text-indigo-500" />;
      case 'documented': return <CheckCircleIcon className="w-4 h-4 text-green-600" />;
      default: return <ClockIcon className="w-4 h-4 text-gray-500" />;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-8">
          {/* Header */}
          <div className="flex items-start justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">{incident.title}</h2>
              <div className="flex items-center space-x-4">
                <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full border ${getPriorityColor(incident.priority || 'low')}`}>
                  {(incident.priority || 'low').toUpperCase()}
                </span>
                <div className="flex items-center space-x-2">
                  {getStatusIcon(incident.status || 'pending')}
                  <span className="text-sm text-gray-600 capitalize">{incident.status || 'pending'}</span>
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            >
              <XCircleIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Column */}
            <div className="space-y-6">
              {/* Incident Details */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                  <TagIcon className="w-5 h-5 mr-2" />
                  Incident Details
                </h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Type</label>
                    <p className="text-sm text-gray-900">{incident.incident_type}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Description</label>
                    <p className="text-sm text-gray-900">{incident.description}</p>
                  </div>
                  {incident.notes && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Notes</label>
                      <p className="text-sm text-gray-900">{incident.notes}</p>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Investigation Notes</label>
                    <p className="text-sm text-gray-900 bg-yellow-50 p-3 rounded border-l-4 border-yellow-400">
                      {incident.comment1 || 'No investigation notes available'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Location Information */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                  <MapPinIcon className="w-5 h-5 mr-2" />
                  Location
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-700">District:</span>
                    <span className="text-sm text-gray-900">{incident.district || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-700">Constituency:</span>
                    <span className="text-sm text-gray-900">{incident.constituency || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-700">Ward:</span>
                    <span className="text-sm text-gray-900">{incident.ward || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-700">Center:</span>
                    <span className="text-sm text-gray-900">{incident.center || 'N/A'}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-6">
              {/* Reporter Information */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                  <UserIcon className="w-5 h-5 mr-2" />
                  Reporter
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-700">Name:</span>
                    <span className="text-sm text-gray-900">{incident.reporter_name || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-700">Phone:</span>
                    <span className="text-sm text-gray-900">{incident.reporter_phone || 'N/A'}</span>
                  </div>
                </div>
              </div>

              {/* Timeline */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                  <CalendarIcon className="w-5 h-5 mr-2" />
                  Timeline
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-700">Created:</span>
                    <span className="text-sm text-gray-900">
                      {new Date(incident.created_at).toLocaleString()}
                    </span>
                  </div>
                  {incident.updated_at && (
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-700">Updated:</span>
                      <span className="text-sm text-gray-900">
                        {new Date(incident.updated_at).toLocaleString()}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <div className="flex flex-wrap gap-3 justify-end">
              <button
                onClick={onClose}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
              >
                Close
              </button>
              {/* Show action buttons based on incident status */}
              {incident.status === 'sent_for_intervention' && (
                <>
                  <button
                    onClick={() => onMarkIntervened(incident)}
                    className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium flex items-center space-x-2"
                  >
                    <ShieldCheckIcon className="w-4 h-4" />
                    <span>Mark as Intervened</span>
                  </button>
                  <button
                    onClick={() => onMarkResolved(incident)}
                    className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium flex items-center space-x-2"
                  >
                    <CheckCircleIcon className="w-4 h-4" />
                    <span>Mark as Resolved</span>
                  </button>
                </>
              )}
              {incident.status === 'intervened' && (
                <button
                  onClick={() => onMarkResolved(incident)}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium flex items-center space-x-2"
                >
                  <CheckCircleIcon className="w-4 h-4" />
                  <span>Mark as Resolved</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

interface CommentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void;
  incident: Incident | null;
  actionType: 'intervened' | 'resolved';
  comment: string;
  setComment: (comment: string) => void;
}

const CommentModal: React.FC<CommentModalProps> = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  incident, 
  actionType, 
  comment, 
  setComment 
}) => {
  if (!isOpen || !incident) return null;

  const actionTitles = {
    intervened: 'Mark as Intervened',
    resolved: 'Mark as Resolved'
  };

  const actionColors = {
    intervened: 'indigo',
    resolved: 'green'
  };

  const color = actionColors[actionType];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {actionTitles[actionType]}
          </h3>
          
          <div className="mb-4">
            <p className="text-sm text-gray-600 mb-2">
              <strong>Incident:</strong> {incident.title}
            </p>
            <p className="text-sm text-gray-600">
              You are about to {actionType === 'intervened' ? 'mark this incident as intervened' : 'mark this incident as resolved'}.
            </p>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Comment <span className="text-red-500">*</span>
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={`Enter your comment for ${actionType}...`}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
              rows={4}
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              This comment will be recorded with the status change.
            </p>
          </div>

          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
            >
              Cancel
            </button>
            <button
              onClick={onSubmit}
              disabled={!comment.trim()}
              className={`flex-1 px-4 py-2 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed
                ${color === 'indigo' ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-green-600 hover:bg-green-700'}`}
            >
              {actionTitles[actionType]}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const IncidentsIntervention: React.FC = () => {
  const [selectedDistrict, setSelectedDistrict] = useState<string>('');
  const [selectedIncident, setSelectedIncident] = useState<Incident | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [actionType, setActionType] = useState<'intervened' | 'resolved'>('intervened');
  const [comment, setComment] = useState('');

  // Fetch districts for filtering
  const { data: complianceData } = useQuery(
    ['district-compliance-for-intervention'],
    () => api.dashboard.getDistrictCompliance(),
    { refetchInterval: 30000 }
  );

  // Fetch incidents with sent_for_intervention status
  const { data: incidentsData, isLoading: incidentsLoading, refetch: refetchIncidents } = useQuery(
    ['incidents-intervention', selectedDistrict],
    () => {
      console.log('🚀 Making API call to fetch intervention incidents...');
      console.log('🔍 API call parameters:', {
        district_id: selectedDistrict || undefined,
        limit: 100000,
        page: 1,
        per_page: 100000
      });
      return api.incidents.getAll({
        district_id: selectedDistrict || undefined,
        limit: 100000,
        page: 1,
        per_page: 100000
      });
    },
    {
      refetchInterval: 30000,
      onSuccess: (data) => {
        console.log('✅ Intervention incidents data received:', data);
        console.log('📊 Raw API response structure:', {
          data: data?.data,
          dataData: data?.data?.data,
          dataIncidents: data?.data?.incidents,
          responseKeys: Object.keys(data || {}),
          dataKeys: Object.keys(data?.data || {}),
        });
      },
      onError: (error) => {
        console.error('❌ Error fetching intervention incidents:', error);
      }
    }
  );

  // Fetch incident stats
  const { data: statsData } = useQuery(
    ['incidents-stats-intervention', selectedDistrict],
    () => api.incidents.getStats({
      district_id: selectedDistrict || undefined,
      limit: 100000,
      page: 1,
      per_page: 100000
    }),
    {
      refetchInterval: 30000,
      onSuccess: (data) => {
        console.log('✅ Intervention stats data received:', data);
      },
      onError: (error) => {
        console.error('❌ Error fetching intervention stats:', error);
      }
    }
  );

  const districts = complianceData?.data?.data?.districts || [];

  // Debug incidents data structure
  console.log('🔍 Full intervention incidents API response:', incidentsData);
  console.log('🔍 Intervention incidents data path:', incidentsData?.data);
  console.log('🔍 Intervention incidents array:', incidentsData?.data?.data);

  // Try different possible data paths
  const allIncidents: Incident[] = incidentsData?.data?.data ||
                                  incidentsData?.data?.incidents ||
                                  incidentsData?.data ||
                                  [];

  console.log('🎯 All intervention incidents array:', allIncidents, allIncidents.length);

  // Filter incidents to only show sent_for_intervention status
  console.log('🔍 All incidents before filtering:', allIncidents.length);
  console.log('📋 Sample incidents (first 3):', allIncidents.slice(0, 3).map(i => ({ id: i.id, title: i.title, status: i.status })));
  console.log('📊 Status distribution:', {
    reported: allIncidents.filter(i => i.status === 'reported').length,
    investigating: allIncidents.filter(i => i.status === 'investigating').length,
    sent_for_investigation: allIncidents.filter(i => i.status === 'sent_for_investigation').length,
    sent_for_intervention: allIncidents.filter(i => i.status === 'sent_for_intervention').length,
    resolved: allIncidents.filter(i => i.status === 'resolved').length,
    dismissed: allIncidents.filter(i => i.status === 'dismissed').length,
    documented: allIncidents.filter(i => i.status === 'documented').length,
    intervened: allIncidents.filter(i => i.status === 'intervened').length,
    uncompliant: allIncidents.filter(i => i.status === 'uncompliant').length,
  });

  // Filter incidents to show sent_for_intervention, intervened, and resolved statuses
  const interventionStatuses = ['sent_for_intervention', 'intervened', 'resolved'];
  const incidents = allIncidents.filter(incident => interventionStatuses.includes(incident.status || ''));
  console.log('🎯 Filtered intervention incidents:', incidents.length);
  console.log('📋 Intervention incidents:', incidents.map(i => ({ id: i.id, title: i.title, status: i.status })));

  // Debug: Log the first incident to see its structure
  if (incidents.length > 0) {
    console.log('📋 First intervention incident structure:', incidents[0]);
    console.log('📍 Location fields in first intervention incident:', {
      district: incidents[0].district,
      district_name: incidents[0].district_name,
      constituency: incidents[0].constituency,
      ward: incidents[0].ward,
      center: incidents[0].center,
      polling_center: incidents[0].polling_center
    });
  }

  // Calculate category stats based on ONLY the incidents displayed in the table (intervention statuses)
  const categoryStats = incidents.reduce((acc, incident) => {
    const category = incident.category;
    // Only include valid categories, exclude unwanted ones
    if (category && !['Administrative', 'Technical', 'Elections', 'Electoral', 'Unknown', 'Governance/HR', 'Health/Environment'].includes(category)) {
      if (!acc[category]) {
        acc[category] = {
          total: 0,
          by_severity: { low: 0, medium: 0, high: 0, critical: 0 }
        };
      }
      acc[category].total += 1;
      const severity = (incident.priority || incident.severity || 'low');
      if (acc[category].by_severity[severity] !== undefined) {
        acc[category].by_severity[severity] += 1;
      }
    }
    return acc;
  }, {} as Record<string, { total: number; by_severity: { low: number; medium: number; high: number; critical: number } }>);
  
  const stats: IncidentStats = statsData?.data?.data || {
    total: allIncidents.length || 0,
    reported: allIncidents.filter(i => i.status === 'reported').length || 0,
    investigating: allIncidents.filter(i => i.status === 'investigating').length || 0,
    resolved: allIncidents.filter(i => i.status === 'resolved').length || 0,
    dismissed: allIncidents.filter(i => i.status === 'dismissed').length || 0,
    uncompliant: allIncidents.filter(i => i.status === 'uncompliant').length || 0,
    sent_for_investigation: allIncidents.filter(i => i.status === 'sent_for_investigation').length || 0,
    sent_for_intervention: allIncidents.filter(i => i.status === 'sent_for_intervention').length || 0,
    intervened: allIncidents.filter(i => i.status === 'intervened').length || 0,
    documented: allIncidents.filter(i => i.status === 'documented').length || 0,
    by_priority: {
      low: allIncidents.filter(i => (i.priority || i.severity) === 'low').length || 0,
      medium: allIncidents.filter(i => (i.priority || i.severity) === 'medium').length || 0,
      high: allIncidents.filter(i => (i.priority || i.severity) === 'high').length || 0,
      critical: allIncidents.filter(i => (i.priority || i.severity) === 'critical').length || 0
    },
    by_category: Object.keys(categoryStats).reduce((acc, category) => {
      acc[category] = categoryStats[category].total;
      return acc;
    }, {} as Record<string, number>)
  };

  const handleMarkIntervened = (incident: Incident) => {
    setSelectedIncident(incident);
    setActionType('intervened');
    setComment('');
    setShowCommentModal(true);
  };

  const handleMarkResolved = (incident: Incident) => {
    setSelectedIncident(incident);
    setActionType('resolved');
    setComment('');
    setShowCommentModal(true);
  };

  const handleSubmitAction = async () => {
    if (!selectedIncident || !comment.trim()) {
      toast.error('Please enter a comment');
      return;
    }

    try {
      await api.incidents.updateStatus(
        selectedIncident.id.toString(),
        actionType,
        comment.trim()
      );

      const actionMessages = {
        intervened: 'Incident marked as intervened successfully!',
        resolved: 'Incident marked as resolved successfully!'
      };

      toast.success(actionMessages[actionType]);
      refetchIncidents();
      setShowPreview(false);
      setShowCommentModal(false);
      setComment('');
      setSelectedIncident(null);
    } catch (error) {
      toast.error(`Failed to ${actionType} incident`);
      console.error('Error updating incident status:', error);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'text-red-600 bg-red-50 border-red-200';
      case 'high': return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'medium': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'low': return 'text-blue-600 bg-blue-50 border-blue-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'resolved': return <CheckCircleIcon className="w-4 h-4 text-green-500" />;
      case 'investigating': return <ExclamationCircleIcon className="w-4 h-4 text-blue-500" />;
      case 'reported': return <ExclamationTriangleIcon className="w-4 h-4 text-purple-500" />;
      case 'sent_for_investigation': return <ExclamationCircleIcon className="w-4 h-4 text-indigo-500" />;
      case 'sent_for_intervention': return <ExclamationTriangleIcon className="w-4 h-4 text-purple-500" />;
      case 'intervened': return <ShieldCheckIcon className="w-4 h-4 text-indigo-500" />;
      case 'documented': return <CheckCircleIcon className="w-4 h-4 text-green-600" />;
      case 'uncompliant': return <XCircleIcon className="w-4 h-4 text-red-500" />;
      case 'dismissed': return <XCircleIcon className="w-4 h-4 text-gray-500" />;
      default: return <ClockIcon className="w-4 h-4 text-gray-500" />;
    }
  };

  console.log('🔄 Loading state:', { incidentsLoading, hasData: !!incidentsData });

  if (incidentsLoading && !incidentsData) {
    console.log('⏳ Showing loading spinner...');
    return <LoadingSpinner fullScreen />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-indigo-50">
      <div className="container px-4 py-8 mx-auto sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="relative overflow-hidden bg-white shadow-xl rounded-2xl">
            <div className="absolute inset-0 bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 opacity-10"></div>
            <div className="relative px-8 py-12 text-center">
              <div className="inline-flex items-center px-4 py-2 mb-4 text-sm font-medium text-purple-700 bg-purple-100 rounded-full">
                <div className="w-2 h-2 mr-2 bg-purple-600 rounded-full animate-pulse"></div>
                Intervention Management
              </div>
              <h1 className="mb-4 text-4xl font-bold text-transparent xl:text-5xl bg-gradient-to-r from-gray-900 via-purple-900 to-indigo-900 bg-clip-text">
                Incidents Intervention Center
              </h1>
              <p className="max-w-2xl mx-auto text-lg text-gray-600 xl:text-xl">
                Manage incidents in the intervention workflow. View incidents sent for intervention, intervened, and resolved statuses across all districts.
              </p>
            </div>
          </div>
        </div>

        {/* District Filter & Stats */}
        <div className="grid grid-cols-1 gap-6 mb-8 lg:grid-cols-4">
          {/* Filters */}
          <div className="lg:col-span-1">
            <div className="p-4 bg-white border border-gray-200 rounded-2xl shadow-lg">
              <h3 className="mb-3 text-lg font-semibold text-gray-900 flex items-center">
                <FunnelIcon className="w-5 h-5 mr-2" />
                Filters
              </h3>

              {/* District Filter */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">District</label>
                <select
                  value={selectedDistrict}
                  onChange={(e) => setSelectedDistrict(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                >
                  <option value="">All Districts</option>
                  {districts.map((district: any) => (
                    <option key={district.district_id} value={district.district_id}>
                      {district.district_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="lg:col-span-3">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div className="p-4 bg-white border border-purple-200 rounded-xl shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-purple-600">Sent for Intervention</p>
                    <p className="text-2xl font-bold text-purple-700">{stats.sent_for_intervention}</p>
                  </div>
                  <ExclamationTriangleIcon className="w-8 h-8 text-purple-400" />
                </div>
              </div>

              <div className="p-4 bg-white border border-indigo-200 rounded-xl shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-indigo-600">Intervened</p>
                    <p className="text-2xl font-bold text-indigo-700">{stats.intervened}</p>
                  </div>
                  <ShieldCheckIcon className="w-8 h-8 text-indigo-400" />
                </div>
              </div>

              <div className="p-4 bg-white border border-green-200 rounded-xl shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-green-600">Resolved</p>
                    <p className="text-2xl font-bold text-green-700">{stats.resolved}</p>
                  </div>
                  <CheckCircleIcon className="w-8 h-8 text-green-400" />
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Category Stats */}
        <div className="mb-8">
          <div className="bg-white border border-gray-200 rounded-2xl shadow-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Incidents by Category</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {categoryStats && Object.entries(categoryStats).map(([category, data]) => (
                <div key={category} className="p-4 bg-gray-50 rounded-lg border">
                  <div className="text-sm font-medium text-gray-900 truncate mb-2" title={category}>
                    {category}
                  </div>
                  <div className="text-xl font-bold text-gray-700 mb-3">{data.total}</div>
                  <div className="grid grid-cols-2 gap-1 text-xs">
                    <div className="flex items-center justify-between bg-red-50 px-2 py-1 rounded border border-red-200">
                      <span className="text-red-600">Critical</span>
                      <span className="font-semibold text-red-700">{data.by_severity.critical}</span>
                    </div>
                    <div className="flex items-center justify-between bg-orange-50 px-2 py-1 rounded border border-orange-200">
                      <span className="text-orange-600">High</span>
                      <span className="font-semibold text-orange-700">{data.by_severity.high}</span>
                    </div>
                    <div className="flex items-center justify-between bg-yellow-50 px-2 py-1 rounded border border-yellow-200">
                      <span className="text-yellow-600">Medium</span>
                      <span className="font-semibold text-yellow-700">{data.by_severity.medium}</span>
                    </div>
                    <div className="flex items-center justify-between bg-blue-50 px-2 py-1 rounded border border-blue-200">
                      <span className="text-blue-600">Low</span>
                      <span className="font-semibold text-blue-700">{data.by_severity.low}</span>
                    </div>
                  </div>
                </div>
              ))}
              {(!categoryStats || Object.keys(categoryStats).length === 0) && (
                <div className="col-span-full text-center py-4 text-gray-500">
                  No category data available
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Incidents Table */}
        <div className="bg-white border border-gray-200 rounded-2xl shadow-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">
              Intervention Incidents {selectedDistrict && `in ${districts.find((d: any) => d.district_id === selectedDistrict)?.district_name}`}
            </h3>
            <p className="text-sm text-gray-600">
              {incidents.length} incident{incidents.length !== 1 ? 's' : ''} awaiting intervention
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Incident</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Severity</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reporter</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {incidents.map((incident) => (
                  <tr key={incident.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{incident.title}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{incident.category || 'Unknown'}</div>
                      {incident.subcategory && (
                        <div className="text-sm text-gray-500 truncate" title={incident.subcategory}>
                          {incident.subcategory.length > 30 ? incident.subcategory.substring(0, 30) + '...' : incident.subcategory}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{incident.district || incident.district_name || 'N/A'}</div>
                      {(incident.center || incident.polling_center) && (
                        <div className="text-sm text-gray-500">{incident.center || incident.polling_center}</div>
                      )}
                      {incident.constituency && (
                        <div className="text-sm text-gray-500">{incident.constituency}</div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor((incident.priority || incident.severity) || 'low')}`}>
                        {((incident.priority || incident.severity || 'low').charAt(0).toUpperCase() + (incident.priority || incident.severity || 'low').slice(1))}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        {getStatusIcon(incident.status || 'pending')}
                        <span className="text-sm text-gray-900 capitalize">{incident.status || 'pending'}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{incident.reporter_name}</div>
                      <div className="text-sm text-gray-500">{incident.reporter_phone}</div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {new Date(incident.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => {
                            setSelectedIncident(incident);
                            setShowPreview(true);
                          }}
                          className="p-2 text-gray-400 hover:text-purple-600 rounded-lg hover:bg-purple-50"
                          title="Preview"
                        >
                          <EyeIcon className="w-4 h-4" />
                        </button>
                        {/* Show action buttons based on incident status */}
                        {incident.status === 'sent_for_intervention' && (
                          <>
                            <button
                              onClick={() => handleMarkIntervened(incident)}
                              className="p-2 text-gray-400 hover:text-indigo-600 rounded-lg hover:bg-indigo-50"
                              title="Mark as Intervened"
                            >
                              <ShieldCheckIcon className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleMarkResolved(incident)}
                              className="p-2 text-gray-400 hover:text-green-600 rounded-lg hover:bg-green-50"
                              title="Mark as Resolved"
                            >
                              <CheckCircleIcon className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        {incident.status === 'intervened' && (
                          <button
                            onClick={() => handleMarkResolved(incident)}
                            className="p-2 text-gray-400 hover:text-green-600 rounded-lg hover:bg-green-50"
                            title="Mark as Resolved"
                          >
                            <CheckCircleIcon className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {incidents.length === 0 && (
              <div className="text-center py-12">
                <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No intervention incidents found</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {selectedDistrict ? 'No incidents sent for intervention found for the selected district.' : 'No incidents with "sent_for_intervention" status have been found.'}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Preview Modal */}
        <PreviewModal
          incident={selectedIncident}
          isOpen={showPreview}
          onClose={() => {
            setShowPreview(false);
            setSelectedIncident(null);
          }}
          onMarkIntervened={handleMarkIntervened}
          onMarkResolved={handleMarkResolved}
        />

        {/* Comment Modal */}
        <CommentModal
          isOpen={showCommentModal}
          onClose={() => {
            setShowCommentModal(false);
            setComment('');
            setSelectedIncident(null);
          }}
          onSubmit={handleSubmitAction}
          incident={selectedIncident}
          actionType={actionType}
          comment={comment}
          setComment={setComment}
        />
      </div>
    </div>
  );
};

export default IncidentsIntervention;
